import React, { useState, useEffect } from 'react';
import cityAPI from '../../services/cityAPI';
import { useTranslate } from '../../Hook/useTranslate';
import './CitySelector.css';

const CitySelector = ({ onCitySelect, selectedCity }) => {
  const t = useTranslate();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  // Fetch cities on component mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoading(true);
        const data = await cityAPI.getPublicCities();
        
        if (Array.isArray(data) && data.length > 0) {
          // Sort cities alphabetically, with popular cities first
          const sortedCities = data.sort((a, b) => {
            if (a.isPopular === b.isPopular) {
              return a.name.localeCompare(b.name);
            }
            return b.isPopular - a.isPopular;
          });
          setCities(sortedCities);
          setError(null);
        } else {
          setError('No cities available. Please add cities to the database.');
          setCities([]);
        }
      } catch (err) {
        console.error('Failed to fetch cities:', err);
        setError(`Failed to load cities: ${err.message}`);
        setCities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, []);

  const handleCitySelect = (city) => {
    onCitySelect(city);
    setIsOpen(false);
  };

  if (loading) {
    return (
      <div className="city-selector-container">
        <div className="city-selector-dropdown-btn animate-pulse" style={{ background: '#f3f4f6', minWidth: '110px', height: '36px', borderRadius: '20px', border: '1px solid #e5e7eb' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="city-selector-container">
        <div style={{ padding: '12px', color: '#EF4444', fontSize: '13px', textAlign: 'center' }}>
          ⚠️ {error}
        </div>
      </div>
    );
  }

  if (!cities || cities.length === 0) {
    return (
      <div className="city-selector-container">
        <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
          No cities available. Run: <code style={{ background: '#f0f0f0', padding: '2px 4px', borderRadius: '3px' }}>npm run seed:cities</code>
        </div>
      </div>
    );
  }

  return (
    <div className="city-selector-container">
      <div className="city-selector-label">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#000"/>
        </svg>
        {t("Select City")}
      </div>

      <div className="city-selector-wrapper">
        <button 
          className="city-selector-button"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="city-selector-value">
            {selectedCity ? selectedCity.name : t('Choose a city...')}
          </span>
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none"
            className={`dropdown-icon ${isOpen ? 'open' : ''}`}
          >
            <path d="M6 9l6 6 6-6" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        {isOpen && (
          <div className="city-selector-dropdown">
            <div className="city-selector-list">
              {cities.length > 0 ? (
                cities.map(city => (
                  <button
                    key={city._id}
                    className={`city-selector-item ${selectedCity?._id === city._id ? 'selected' : ''}`}
                    onClick={() => handleCitySelect(city)}
                  >
                    <span>{city.name}</span>
                    {selectedCity?._id === city._id && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M6 10l6 6 12-12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    )}
                  </button>
                ))
              ) : (
                <div className="city-selector-empty">{t("No cities available")}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CitySelector;
